import React, { useState, useEffect } from "react";
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Flex,
  Icon,
  Text,
  Button,
  VStack,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdEvStation, MdHistory, MdLocationOn } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import ChargingManagementPanel from "../tab_panels/ChargingManagementPanel";
import SessionsPanel from "../tab_panels/SessionsPanel";

const LocationDetailsTabs = (props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const clickHandler = (index) => {
    if (index === 0) {
      navigate(`/admin/charging_management/view/${props.id}/chargers`);
    } else if (index === 1) {
      navigate(`/admin/charging_management/view/${props.id}/sessions`);
    }
  };

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/sessions")) {
      setActiveTabIndex(1);
    } else {
      setActiveTabIndex(0);
    }
  }, [location.pathname]);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Back Button */}
      <Flex justify="flex-end" mb={4}>
        <Button
          bgColor="brand.500"
          _hover={{ bgColor: "#d44e00" }}
          onClick={() => navigate("/admin/charging_management")}
          color="white"
          px={{ base: 4, md: 5 }}
          py={{ base: 2, md: 3 }}
          fontSize={{ base: "sm", md: "md" }}
          borderRadius={8}
        >
          Back
        </Button>
      </Flex>

      {/* Location Details Card - Sticky */}
      <Box
        position="sticky"
        top={{ base: "60px", md: "70px" }}
        zIndex={0}
        bg={bgColor}
        pb={4}
      >
        <Box
          bg={cardBg}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          boxShadow="sm"
        >
          <VStack align="stretch" spacing={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Icon as={MdLocationOn} boxSize={{ base: 5, md: 6 }} color="brand.500" />
              <Text fontSize={{ base: "lg", md: "xl", lg: "2xl" }} fontWeight="bold" color={textColor}>
                {props.data?.Name || "Testing Station"}
              </Text>
            </Flex>
            
            <Divider />
            
            <Flex justify="space-between" align="start" flexWrap="wrap" gap={{ base: 2, md: 4 }}>
              <VStack align="stretch" spacing={1} flex={{ base: "1 1 100%", md: "1 1 45%" }}>
                <Text fontSize={{ base: "2xs", md: "xs" }} color={labelColor} fontWeight="semibold">Address</Text>
                <Text 
                  fontSize={{ base: "sm", md: "md" }} 
                  fontWeight="medium"
                  noOfLines={{ base: 2, md: 1 }}
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {(() => {
                    const address = props.data?.Address || "N/A";
                    if (address.length > 60) {
                      return address.substring(0, 60) + "...";
                    }
                    return address;
                  })()}
                </Text>
              </VStack>
              
              <VStack align="stretch" spacing={1}>
                <Text fontSize={{ base: "2xs", md: "xs" }} color={labelColor} fontWeight="semibold">City</Text>
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">{props.data?.City || "Chilakluripet"}</Text>
              </VStack>
              
              <VStack align="stretch" spacing={1}>
                <Text fontSize={{ base: "2xs", md: "xs" }} color={labelColor} fontWeight="semibold">State</Text>
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">{props.data?.State || "Andhra Pradesh"}</Text>
              </VStack>
            </Flex>
          </VStack>
        </Box>
      </Box>

      <Tabs
        variant="unstyled"
        isLazy={true}
        index={activeTabIndex}
        style={{ marginTop: "20px" }}
      >
        <TabList
          gap={4}
          px={1}
          py={2}
          bg="rgba(255, 255, 255, 0.6)"
          backdropFilter="blur(12px)"
          borderRadius="xl"
          border="1px solid rgba(35, 169, 121, 0.15)"
          position="relative"
          overflow="hidden"
        >
          <Tab
            _focus={{ boxShadow: "none" }}
            onClick={() => clickHandler(0)}
            borderRadius="lg"
            px={5}
            py={2.5}
            fontWeight="500"
            color="gray.600"
            position="relative"
            zIndex="1"
            _selected={{
              bg: "rgba(35, 169, 121, 0.15)",
              color: "brand.500",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(35, 169, 121, 0.1)",
            }}
            _hover={{
              bg: "rgba(35, 169, 121, 0.08)",
              color: "brand.500",
            }}
          >
            <Flex align="center" gap={2}>
              <Icon as={MdEvStation} boxSize={5} />
              <Text>Chargers</Text>
            </Flex>
          </Tab>

          <Tab
            _focus={{ boxShadow: "none" }}
            onClick={() => clickHandler(1)}
            borderRadius="lg"
            px={5}
            py={2.5}
            fontWeight="500"
            color="gray.600"
            position="relative"
            zIndex="1"
            _selected={{
              bg: "rgba(35, 169, 121, 0.15)",
              color: "brand.500",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(35, 169, 121, 0.1)",
            }}
            _hover={{
              bg: "rgba(35, 169, 121, 0.08)",
              color: "brand.500",
            }}
          >
            <Flex align="center" gap={2}>
              <Icon as={MdHistory} boxSize={5} />
              <Text>Sessions</Text>
            </Flex>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            <ChargingManagementPanel
              locationId={props.id}
              locationData={props.data}
            />
          </TabPanel>
          <TabPanel p={0}>
            <SessionsPanel locationId={props.id} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default LocationDetailsTabs;
